<template>
    <div v-dusk="'delivery-datepicker'" class="row">
        <div v-if="loading" class="col-md-12">
            <i class="fas fa-spinner fa-spin"></i> Chargement des dates de livraison...
        </div>
        <div v-else-if="errors && errors.length" class="col-md-12">
            <div class="alert alert-danger">
                <template v-for="error in errors">
                    <span>{{ error }}</span>
                </template>
                <br>
                Essayez de resélectionner le magasin.
            </div>
        </div>
        <template v-else-if="loaded">
            <div class="col-md-4">
                <BootstrapDatepicker
                    ref="datepicker"
                    class="delivery-datepicker mb-2"
                    v-model="selectedDate"
                    :disabled-dates="disabledDates"
                    :day-cell-content="getDayCellContent"
                />
            </div>
            <div class="col-md-8" v-if="!isRecurrent">
                <button type="button" class="btn btn-outline-warning mb-2" @click="openWithNotUsualDeliveryDaysSelectable">
                    Choisir une date exceptionnelle
                </button>
                <button :disabled="!canSelectRedDates" type="button" class="btn mb-2" :class="canSelectRedDates ? 'btn-outline-danger' : 'btn-outline-secondary'" @click="openWithNotAllowedDeliveryDaysSelectable">
                    Choisir une date non-autorisée
                </button>
            </div>
        </template>
        <div v-else class="col-md-12">
            <div class="alert alert-danger">
                Erreur lors du chargement des dates de livraison. Essayez de resélectionner le magasin.
            </div>
        </div>
    </div>
</template>

<script>
    import BootstrapDatepicker from './BootstrapDatepicker';

    import {useGetLoader} from './compositions/axios/loadingRequest';
    import {momentDay, momentDaysAreSame} from "../moment-with-helpers";

    export default {
        components: {
            BootstrapDatepicker,
        },

        props: {
            order: {
                type: Object,
                required: false,
            },
            customer: {
                type: Object,
                required: true,
            },
            value: {
                type: Object,
                required: false,
            },
            isRecurrent: {
                type: Boolean,
                required: false,
                default: false,
            },
            canSelectRedDates: {
                type: Boolean,
                required: true,
            },
        },

        setup() {
            const {
                loaded,
                loading,
                errors,
                load,
            } = useGetLoader();

            return {
                loaded,
                loading,
                errors,
                load,
            };
        },

        data: function () {
            return {
                selectedDate: this.value && this.value.date,

                calendar: null,

                restrictedDaysSelectable: false,
                forbiddenDaysSelectable: false,
            };
        },

        created() {
            this.loadDates();
        },

        methods: {
            getDayCellContent(params) {
                const datePermission = this.getDatePermission(new Date(params.timestamp));
                let color = 'blank';
                if(datePermission) {
                    if (datePermission.higherWarnLevel === 'restricted') {
                        color = 'orange';
                    } else if (datePermission.higherWarnLevel === 'forbidden') {
                        color = 'red';
                    } else if (datePermission.higherWarnLevel === 'allowed') {
                        color = 'green';
                    }
                }
                return `<span class="day-${color}">${params.date}</span>`;
            },

            getDatePermission(date) {
                if (this.calendar && this.calendar.authorizations) {
                    let authorization = this.calendar.authorizations[momentDay(date).format('YYYY-MM-DD')];
                    if (!authorization) {
                        authorization = {
                            authorizations: [
                                {
                                    permission: "too_far_away_in_past_or_future",
                                    reason: "Jour trop loin dans le passé ou dans le futur",
                                    warnLevel: "restricted",
                                }
                            ],
                            higherWarnLevel: 'forbidden',
                        }
                    }
                    authorization['date'] = momentDay(date).toDate();
                    return authorization || null;
                }
            },

            open() {
                this.$refs.datepicker.close();
                this.$refs.datepicker.showCalendar();
                this.$refs.datepicker.$el.querySelector('input').focus();
                this.$refs.datepicker.$el.querySelector('input').addEventListener('blur', () => {
                    if (this.onClose) {
                        this.onClose();
                    }
                });

                this.$emit('opened');
            },

            openWithNotUsualDeliveryDaysSelectable() {
                this.resetFlags();
                this.restrictedDaysSelectable = true;

                this.open();
            },

            openWithNotAllowedDeliveryDaysSelectable() {
                this.resetFlags();
                this.forbiddenDaysSelectable = true;

                this.open();
            },

            resetFlags() {
                this.restrictedDaysSelectable = false;
                this.forbiddenDaysSelectable = false;
            },

            async loadDates() {
                this.resetFlags();
                let url = `/espace-employes/commandes/calendrier-dates-livraison` + (this.isRecurrent ? '-commandes-recurrentes' : '') + `/${this.customer.type}/${this.customer.id}`;
                if(this.order) {
                    url += `/${this.order.id}`
                }
                const data = await this.load(url);
                if (data) {
                    this.calendar = data;
                }
            },

            buildEventDateChangePayload(date) {
                if (date) {
                    return this.getDatePermission(date);
                }
                return null;
            },
        },

        watch: {
            customer(newCustomer) {
                this.selectedDate = null;
                if (newCustomer) {
                    this.loadDates();
                }
            },

            selectedDate(newDate, oldDate) {
                if ((newDate === null && oldDate !== null)
                 || (newDate !== null && oldDate === null)
                 || (newDate !== null && oldDate !== null && !momentDaysAreSame(newDate, oldDate))) {
                    this.$emit('input', this.buildEventDateChangePayload(newDate));
                }
            },
        },

        computed: {
            disabledDates() {
                return {
                    customPredictor: (date) => {
                        const permission = this.getDatePermission(date);
                        if (permission) {
                            const type = this.getDatePermission(date).higherWarnLevel;

                            if (type === 'forbidden' && !this.forbiddenDaysSelectable) {
                                return true;
                            }
                            if (type === 'restricted' && !this.restrictedDaysSelectable && !this.forbiddenDaysSelectable) {
                                return true;
                            }
                        }
                        return false;
                    }
                };
            },
        }
    }
</script>

<style lang="scss">

</style>
